import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AssignWizard from '../../../components/assignWizard/AssignWizard';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import PresetActions from '../../../components/preset/presetActions';
import { PresetCard } from '../../../components/preset/PresetCard';
import PresetsDeviceSchedules from '../../../components/preset/presetDeviceSchedules';
import PresetInputs from '../../../components/preset/presetInputs';
import PresetMobile from '../../../components/preset/presetMobile';
import PresetOutputs from '../../../components/preset/presetOutputs';
import PresetParameters from '../../../components/preset/presetParameters';
import PresetReaders from '../../../components/preset/presetReaders';
import { SystemProfileTypeIds } from '../../../components/preset/presetSettingsWizard';
import { SelectType } from '../../../components/settingFields/SettingSelectField';
import { toast } from "react-toastify";
import { PRESET_TYPES } from '../../../constants/preset-constants';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import ReaderColumns from '../../../models/ColumnDefinitions/ReaderColumns';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { DevicePreset as ReaderPresetModel } from '../../../models/DevicePreset';
import { ListQuery } from '../../../models/ListQuery';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import searchColumns from '../../../models/search/SiteSearch';
import { DeleteEntityResult } from '../../../models/services/DeleteEntityResult';
import { TabSummary } from '../../../models/TabSummary';
import { hideColumns } from '../../../models/utilities/columnHelper';
import { getDeviceProfileNames, getReaderPresetsId, storeReaderPreset } from '../../../services/hardwarePresetService';
import { deletePresetSetting, getReaderPresetMobileSettings } from '../../../services/presetService';
import { assignPresetReaders, getReaders } from '../../../services/readerService';
import { saveSettingData } from '../../../services/settingDataService';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { appendFilter } from '../../../utilities/filters';
import { getSettingDataToEdit } from '../../../utilities/presetUtil';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialItem : ReaderPresetModel = {
    id: 0,
    name: '',
    type: '',
    devices: 0,
    site: '',
    siteId: 0,
    profileTypeId: '',
    pInheritsFrom: '',
    inheritsFromId: 0,
    description: '',
    totalDevices: 0,
    totalActions: 0,
    totalParameters: 0,
    totalInputs: 0,
    totalDoorModePatterns: 0,
    totalOutputs: 0,
    mobileValues: undefined
}

const allItemsPath = '/access-control/hardware/presets';
const itemPath = '/access-control/hardware/reader-preset/';

// Tab indices constants for clarity
const TAB_INDICES = {
    MOBILE:0,
    INPUTS:1,
    OUTPUTS:2,
    ACTIONS:3,
    PARAMETERS:4,
    SCHEDULES:5,
    READERS:6,
    NOTES:7
}

const ReaderPreset: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.mobile), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.inputs), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.outputs), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.actions), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.parameters), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.schedules), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.readers), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<ReaderPresetModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const [showManagedModal, setShowManagedModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const dialog = useDialog();
    const [originalMobileValues, setOriginalMobileValues] = useState<any>();

    const onDelete = (id: string, tabIndex:number): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deletePresetSetting(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setTabs(updateTabBadgeTotal(tabIndex, tabs[tabIndex].badge - 1, tabs));
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const onPresetSave = (tabIndex:number) => {
        setTabs(updateTabBadgeTotal(tabIndex, tabs[tabIndex].badge + 1, tabs));
        // Reload page to get fresh data after save
        reloadPage();
    }

    const onPresetDeleteUpdateBadge = (tabIndex:number) => {
        setTabs(updateTabBadgeTotal(tabIndex, tabs[tabIndex].badge - 1, tabs));
        // Reload page to get fresh data after delete
        reloadPage();
    }

    const tabContentList: ContentWrapper[] = [
        {
            content: <PresetMobile />
        },
        {
            content: <PresetInputs onDelete={(id) => onDelete(id, TAB_INDICES.INPUTS)} currentItemDetails={currentItem} onSave={(createNew) => { if (createNew) onPresetSave(TAB_INDICES.INPUTS) }} profileId={SystemProfileTypeIds.Reader} itemId={itemId} />,
        },
        {
            content: <PresetOutputs itemId={itemId} />,
        },
        {
            content: <PresetActions onDelete={(id) => onDelete(id, TAB_INDICES.ACTIONS)} currentItemDetails={currentItem} onSave={(createNew) => { if (createNew) onPresetSave(TAB_INDICES.ACTIONS) }} profileId={SystemProfileTypeIds.Reader} itemId={itemId} />,
        },
        {
            content: <PresetParameters onSave={(createNew) => { if (createNew) onPresetSave(3) }} profileId={SystemProfileTypeIds.Reader} itemId={itemId} />,
        },
        {
            content: <PresetsDeviceSchedules itemId={itemId} onDelete={(id) => onDelete(id, TAB_INDICES.SCHEDULES)} onSave={(createNew) => { if (createNew) onPresetSave(TAB_INDICES.SCHEDULES) }} profileType={SystemProfileTypeIds.Reader} onDeleteUpdateBadge={(createNew) => { if (createNew) onPresetDeleteUpdateBadge(4) }} />,
        },
        {
            content: <PresetReaders itemId={itemId}>
                        {/* <button
                            type="button"
                            className="flex ml-8 px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                            onClick={() => {
                                setShowManagedModal(true);
                            }}
                        >
                            {translate.byKey(TranslationKeys.manage)}
                        </button> */} {/* Needs backend support for correctly clearing old actions*/}
            </PresetReaders>,
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }];

    const topic: Topic = {
        accessor: 'nameOriginal',
        title: translate.byKey(TranslationKeys.readerPreset),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    const reloadPage = async () => {
        try {
            setTabs([])
            let readerPreset;
            if (itemId) {
                readerPreset = await getReaderPresetsId(itemId);
                const mobileValues  = await getReaderPresetMobileSettings(readerPreset.id);
                setOriginalMobileValues(mobileValues);
                setSelectedItem({label: readerPreset.name, value: readerPreset.id.toString(), isDefault: readerPreset.isDefault})
                setCurrentItem({ ...readerPreset, mobileValues});
                // update the tab badge counts, with correct indices
                const updatedTabs = updateTabBadgeTotal(TAB_INDICES.INPUTS, readerPreset?.totalInputs,
                    updateTabBadgeTotal(TAB_INDICES.OUTPUTS, 0,
                        updateTabBadgeTotal(TAB_INDICES.ACTIONS, readerPreset?.totalActions,
                            updateTabBadgeTotal(TAB_INDICES.PARAMETERS, readerPreset?.totalParameters,
                                updateTabBadgeTotal(TAB_INDICES.SCHEDULES, readerPreset?.totalDoorModePatterns,
                                    updateTabBadgeTotal(TAB_INDICES.READERS, readerPreset?.totalDevices, initialTabs))))));
                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[TAB_INDICES.NOTES]]);
            }

            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.preset) },
                { value: itemPath + `${itemId}`, displayName: `${readerPreset?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    useEffect(() => {
        setTabs([]);
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[TAB_INDICES.NOTES];
        return selectedTabContent.content;
    };

    const updateTabBadgeTotal = (tabIndex:number, totalCount:number, currentTabs:TabSummary[]) => {
        const newTabs = [...currentTabs];
        if (newTabs[tabIndex]){
            newTabs[tabIndex].badge = totalCount;
            newTabs[tabIndex].isActive = true; // Set tab to active so we can show it
        }
        return newTabs;
    };

    const saveReaderPreset = async (values) => {
        try {
            setBusy(true);
            const newReaderPreset: ReaderPresetModel = {
                ...currentItem,
                name: values.name,
                id: values.id,
                pInheritsFrom: values.pInheritsFrom,
                inheritsFromId: values.inheritsFromId,
                siteId: currentItem?.siteId ? currentItem?.siteId : siteId,
                description: values.description
            }
            const isNew = newReaderPreset.id ? false : true;

            if (values.name !== currentItem.name || values.description !== currentItem.description) {
                const response: any = await storeReaderPreset(newReaderPreset, isNew);
                history.push(`${itemPath}${response.id}`);
                setItemId(response.id)
            }

            // Edit mobile settings can only be triggered for an existing preset
            if (!isNew) {
                await editMobileSettings(values);
            }
            toast.success(translate.byKey(TranslationKeys.readerPresetSaved));

            // Reload page to refresh all data and tab counts
            await reloadPage();
        }
        catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }
        finally{
            setBusy(false);
        }
    }

    const editMobileSettings = async (values) => {
        try {
            const allPromises = await Promise.all(getSettingDataToEdit(values, originalMobileValues).map(saveSettingData));
            const allFailed = allPromises.filter((promise) => !promise.success);
            if (allFailed.length > 0) {
               toast.warn(translate.byKey(TranslationKeys.error));
            }
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }
    }

    const dataMethod = (listParameters?: ListQuery) =>
        getReaders(
            translate,
            {
                ...listParameters,
                filters: appendFilter(
                    itemId,
                    "Profile",
                    "profile.id",
                    listParameters?.filters ?? []
                ),
            }
        );

    const getAllPresetReaders = async () => {
        try {
            const response = (await dataMethod()).rows;
            const readerIds = response.map((reader) => reader.id.toString());
            setSelectedIds(readerIds);
            setOldSelectedIds(readerIds);
        } catch (error) {
            showError(error, toast.warn);
        }
    };

    const assignReaders = async () =>{
        const newSelectedIds = selectedIds.filter((value) => value !== "");
        setOldSelectedIds(newSelectedIds);

        const toBeAdded = newSelectedIds.filter((value) => {
            if (!!!oldSelectedIds.includes(value)) {
                return value;
            }
        });

        setBusy(true);
        try {
            await assignPresetReaders(itemId, toBeAdded);
            // Reload the page to get updated tab counts
            await reloadPage();
        } catch (error) {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error),
            });
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (showManagedModal) {
            getAllPresetReaders();
        }
    }, [showManagedModal]);

    return <div className="flex h-full">
        <TopicContextProvider dataMethod={(listParameters) => getDeviceProfileNames(PRESET_TYPES.READER, listParameters)} itemId={itemId} topic={topic} selectedItem={selectedItem}>
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveReaderPreset}
                    tabs={tabs}
                    itemPath={itemPath}
                    initialValues={currentItem}>
                    <PresetCard presetType={SelectType.readerProfile}></PresetCard>
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
        <AssignWizard
            isOpen={showManagedModal}
            onClose={() => setShowManagedModal(false)}
            title={translate.byKey(TranslationKeys.assignReaders)}
            lookup={[]}
            dataMethod={(listParameters?: ListQuery) => getReaders(translate, listParameters)}
            columnDescriptors={
                {
                    columns: hideColumns(ReaderColumns, ['preset', 'door', 'site', 'ipAddress']),
                    searchColumns: searchColumns
                }
            }
            handleOnSubmit={assignReaders}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            alwaysSelectedAndDisabled={oldSelectedIds}
        />
    </div>
}

export default ReaderPreset;
